import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { CatalogTable, CatalogTableColumnsFunc, CatalogTableRow } from '@backstage/plugin-catalog';
import { EntityListDocsTable, DocsTableRow } from '@backstage/plugin-techdocs';

const useStyles = makeStyles(theme => ({
  nameCell: {
    maxWidth: 350,
    overflow: 'hidden',
  },
  name: {
    marginBottom: theme.spacing(0.75),
  },
  description: {
    maxWidth: '65ch',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.secondary,
    fontWeight: 'normal',
    transition: 'all 0s ease',
    lineHeight: 1.2,
    width: 350,
    display: 'block',
  },
}));

// ── Catalog ──────────────────────────────────────────────────────────────────

const CATALOG_COL_MIN_WIDTH = "150px";

function CatalogNameCell({
  entity,
  defaultKind,
}: Pick<CatalogTableRow, 'entity'> & { defaultKind?: string }) {
  const classes = useStyles();
  return (
    <div className={classes.nameCell}>
      <div className={classes.name}>
        <EntityRefLink entityRef={entity} defaultKind={defaultKind || 'Component'} />
      </div>
      {entity.metadata.description && (
        <Tooltip title={entity.metadata.description} placement="top">
          <span className={classes.description}>
            {entity.metadata.description}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

const { columns: catalogColumns } = CatalogTable;

export const customCatalogColumns: CatalogTableColumnsFunc = ({ filters, entities }) => {
  const showTypeColumn = filters.type === undefined;
  const defaultKind = filters.kind?.value;

  const nameColumn = {
    ...catalogColumns.createNameColumn({ defaultKind }),
    render: ({ entity }: CatalogTableRow) => (
      <CatalogNameCell entity={entity} defaultKind={defaultKind} />
    ),
  };

  function createEntitySpecificColumns() {
    const tagColumns = [catalogColumns.createTagsColumn()];
    const baseColumns = [
      catalogColumns.createSystemColumn(),
      { ...catalogColumns.createOwnerColumn(), minWidth: CATALOG_COL_MIN_WIDTH },
      { ...catalogColumns.createSpecTypeColumn({ hidden: !showTypeColumn }), minWidth: CATALOG_COL_MIN_WIDTH  },
    ];

    switch (defaultKind) {
      case 'user':
        return [...tagColumns];
      case 'domain':
      case 'system':
        return [catalogColumns.createOwnerColumn(), ...tagColumns];
      case 'group':
      case 'template':
        return [catalogColumns.createSpecTypeColumn({ hidden: !showTypeColumn }), ...tagColumns];
      case 'location':
        return [
          catalogColumns.createSpecTypeColumn({ hidden: !showTypeColumn }),
          catalogColumns.createSpecTargetsColumn(),
        ];
      default:
        return entities.every(e => e.metadata.namespace === 'default')
          ? [...baseColumns, ...tagColumns]
          : [...baseColumns, catalogColumns.createNamespaceColumn(), ...tagColumns];
    }
  }

  return [
    catalogColumns.createTitleColumn({ hidden: true }),
    nameColumn,
    ...createEntitySpecificColumns(),
  ];
};

// ── Docs ─────────────────────────────────────────────────────────────────────

function DocsNameCell({ entity, docsUrl }: Pick<DocsTableRow, 'entity'> & { docsUrl: string }) {
  const classes = useStyles();
  const title = entity.metadata.title || entity.metadata.name;
  return (
    <div className={classes.nameCell}>
      <div className={classes.name}>
        <Link to={docsUrl}>{title}</Link>
      </div>
      {entity.metadata.description && (
        <Tooltip title={entity.metadata.description} placement="top">
          <span className={classes.description}>
            {entity.metadata.description}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

const { columns: docsColumnFactories } = EntityListDocsTable;

export const customDocsColumns = [
  docsColumnFactories.createTitleColumn({ hidden: true }),
  {
    ...docsColumnFactories.createNameColumn(),
    render: (row: DocsTableRow) => (
      <DocsNameCell entity={row.entity} docsUrl={row.resolved.docsUrl} />
    ),
  },
  docsColumnFactories.createOwnerColumn(),
  docsColumnFactories.createKindColumn(),
  docsColumnFactories.createTypeColumn(),
];
